import React, { useState } from 'react';
import { BookOpen, Clock, User, Star, Filter, Plus, Play, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card.js';


interface MemoryItem {
  id: string;
  text: string;
  tags?: string[];
  metadata?: {
    source?: string;
    createdAt?: string;
  };
}

interface LessonsTabProps {
  searchTerm: string;
  selectedAgent: string;
  selectedType: string;
  items: MemoryItem[];
  loading: boolean;
  error: string | null;
  onDelete: (itemId: string) => void;
  onRefresh: () => void;
}

const LessonsTab: React.FC<LessonsTabProps> = ({ searchTerm, selectedAgent, selectedType, items, loading, error, onDelete, onRefresh }) => {
  const lessons = items.map(item => ({
    id: item.id,
    title: item.text ? (item.text.length > 50 ? item.text.substring(0, 50) + '...' : item.text) : 'Untitled Lesson',
    description: item.text || 'No description available',
    category: 'Learning',
    difficulty: 'Intermediate',
    duration: '10 min',
    author: item.metadata?.source || 'System',
    rating: 4.5,
    tags: item.tags || [],
    lastUpdated: item.metadata?.createdAt ? new Date(item.metadata.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    completions: Math.floor(Math.random() * 100) + 50,
    agent: item.metadata?.source || 'Unknown Agent'
  }));

  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  const filteredLessons = lessons.filter(lesson => {
    const matchesSearch = lesson.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lesson.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lesson.tags.some((tag: string) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesAgent = selectedAgent === 'All' || lesson.agent === selectedAgent;
    const matchesType = selectedType === 'All' || lesson.category.toLowerCase().includes(selectedType.toLowerCase());
    const matchesFilter = filter === 'all' || lesson.difficulty.toLowerCase() === filter;
    
    return matchesSearch && matchesAgent && matchesType && matchesFilter;
  });

  const sortedLessons = [...filteredLessons].sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'popular':
        return b.completions - a.completions;
      case 'recent':
      default:
        return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
    }
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return 'text-green-400 bg-green-400/20';
      case 'intermediate':
        return 'text-yellow-400 bg-yellow-400/20';
      case 'advanced':
        return 'text-orange-400 bg-orange-400/20';
      case 'expert':
        return 'text-red-400 bg-red-400/20';
      default:
        return 'text-slate-400 bg-slate-400/20';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-400">Loading lessons...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">⚠️ {error}</div>
        <Button onClick={onRefresh} variant="ghost">
          <RefreshCw size={16} className="mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gradient text-drop-shadow">Lessons</h2>
          <p className="text-slate-400 mt-1">Learn from curated tutorials and best practices ({lessons.length} entries)</p>
        </div>
        <div className="flex space-x-2">
          <Button size="sm" variant="ghost" onClick={onRefresh}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button size="sm">
            <Plus size={16} className="mr-2" />
            Create Lesson
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="violett-input"
          >
            <option value="all">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
            <option value="expert">Expert</option>
          </select>
        </div>
        
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="violett-input"
        >
          <option value="recent">Most Recent</option>
          <option value="rating">Highest Rated</option>
          <option value="popular">Most Popular</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {sortedLessons.map((lesson) => (
          <Card key={lesson.id} className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300 cursor-pointer group hover:scale-105">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg cream-gradient-text text-drop-shadow group-hover:text-gradient transition-colors mb-2">{lesson.title}</CardTitle>
                  <CardDescription className="cream-gradient-text text-drop-shadow">{lesson.description}</CardDescription>
                </div>
                <div className="flex items-center space-x-1 ml-4">
                  <Star size={14} className="text-yellow-400 fill-current" />
                  <span className="text-sm text-slate-300">{lesson.rating}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(lesson.difficulty)}`}>
                  {lesson.difficulty}
                </span>
                <span className="text-xs text-slate-400 bg-slate-800 px-2 py-1 rounded">
                  {lesson.category}
                </span>
              </div>

              <div className="flex flex-wrap gap-1">
                {lesson.tags.map((tag: string) => (
                  <span key={tag} className="px-2 py-1 text-slate-300 text-xs glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded hover:border-slate-600/50 transition-all duration-300">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between text-xs text-slate-400">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <Clock size={12} />
                    <span>{lesson.duration}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <User size={12} />
                    <span>{lesson.author}</span>
                  </div>
                </div>
                <span>{lesson.completions} completions</span>
              </div>

              <div className="flex space-x-2">
                <Button size="sm" className="flex-1">
                  <Play size={14} className="mr-2" />
                  Start Lesson
                </Button>
                <Button variant="ghost" size="sm">Preview</Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(lesson.id);
                  }}
                  className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {sortedLessons.length === 0 && (
        <div className="text-center py-12">
          <BookOpen size={48} className="mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">No lessons found</h3>
          <p className="text-slate-400">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

export default LessonsTab;
